import React from 'react'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../../config/api'

function formatElapsed(ms) {
  if (ms == null || Number.isNaN(Number(ms)) || Number(ms) < 0) return '—'
  const s = Math.floor(Number(ms) / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ${s % 60}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

function jobTypeLabel(type) {
  const t = String(type || '')
  if (t === 'combine') return 'Combine'
  if (t === 'pipeline') return 'Pipeline'
  if (t === 'enrich') return 'Enrich'
  if (t === 'allocate') return 'Allocate'
  if (t === 'ingest') return 'Ingest'
  return t || 'Job'
}

export default function GamingBackgroundJobsPanel({ onChanged, compact = false }) {
  const [jobs, setJobs] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [busyId, setBusyId] = React.useState(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const resp = await authFetch(getLocalApiUrl('/api/gaming/process/jobs'))
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(json?.error || `Jobs failed: ${resp.status}`)
      setJobs(Array.isArray(json.data) ? json.data : [])
    } catch (err) {
      setJobs([])
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
    const t = setInterval(load, 12000)
    return () => clearInterval(t)
  }, [load])

  const cancel = async (body, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return
    try {
      setBusyId(body.job_id != null ? String(body.job_id) : 'all')
      const resp = await authFetch(getLocalApiUrl('/api/gaming/process/jobs/cancel'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(json?.error || `Cancel failed: ${resp.status}`)
      const n = Number(json.cancelled_count || 0)
      toast.success(n ? `Stopped ${n} job(s)` : 'No running jobs to stop')
      await load()
      if (onChanged) await onChanged()
    } catch (err) {
      toast.error(err?.message || 'Stop job failed')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className={`card mb-3 !p-3 border ${jobs.length ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200'}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Background jobs</h3>
          <p className="text-xs text-gray-600 mt-0.5">
            {jobs.length
              ? `${jobs.length} pending or running across all trading days. Stop closes the ticket; it does not delete meters or sessions.`
              : 'No pending or running combine / pipeline jobs.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary !py-1.5 !px-3 !text-sm" disabled={loading || busyId} onClick={load}>
            Refresh
          </button>
          {jobs.length > 0 && (
            <button
              type="button"
              className="btn-secondary !py-1.5 !px-3 !text-sm"
              disabled={busyId}
              onClick={() => cancel(
                { all_active: true },
                'Stop every pending/running combine and pipeline job?\n\nThis only closes job tickets. It does not delete meters, sessions, or Combined data.'
              )}
            >
              Stop all
            </button>
          )}
        </div>
      </div>
      {jobs.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500 uppercase">
                <th className="pr-3 py-1 font-medium">ID</th>
                <th className="pr-3 py-1 font-medium">Day</th>
                <th className="pr-3 py-1 font-medium">Type</th>
                <th className="pr-3 py-1 font-medium">Status</th>
                {!compact && <th className="pr-3 py-1 font-medium">Phase</th>}
                <th className="pr-3 py-1 font-medium">Age</th>
                <th className="py-1 font-medium" />
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id} className="border-t border-blue-100 text-gray-800">
                  <td className="pr-3 py-1.5 font-mono">{job.id}</td>
                  <td className="pr-3 py-1.5 whitespace-nowrap">{String(job.trading_day || '').slice(0, 10)}</td>
                  <td className="pr-3 py-1.5">{jobTypeLabel(job.job_type)}</td>
                  <td className="pr-3 py-1.5">{job.status}</td>
                  {!compact && (
                    <td className="pr-3 py-1.5 text-gray-600">{job.phase_label || job.phase || '—'}</td>
                  )}
                  <td className="pr-3 py-1.5">{formatElapsed(job.elapsed_ms)}</td>
                  <td className="py-1.5 text-right">
                    <button
                      type="button"
                      className="text-red-700 hover:underline disabled:opacity-50"
                      disabled={busyId}
                      onClick={() => cancel(
                        { job_id: job.id },
                        `Stop job ${job.id} (${jobTypeLabel(job.job_type)} for ${String(job.trading_day || '').slice(0, 10)})?`
                      )}
                    >
                      {busyId === String(job.id) ? 'Stopping…' : 'Stop'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
